import { t } from "@lingui/macro";
import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from "react";
import { ExpandedSettingsPanels } from "./CollapsibleSettingsPanel";

type Section =
	| "company"
	| "call-results"
	| "connections"
	| "tools"
	| "users"
	| "preferences"
	| "developer";
const ActiveSection = createContext<Section>("company");
const readSection = () =>
	new URLSearchParams(window.location.search).get("section") || "company";

export function SettingsNavigation({
	isAdmin,
	developerEnabled,
	children,
}: {
	isAdmin: boolean;
	developerEnabled: boolean;
	children: ReactNode;
}) {
	const [requested, setRequested] = useState(readSection);
	const sections: { id: Section; label: string }[] = [
		...(isAdmin
			? [
					{
						id: "company" as const,
						label: t({ id: "settings.company", message: "Company" }),
					},
				]
			: []),
		{
			id: "call-results",
			label: t({ id: "settings.callResults", message: "Call results" }),
		},
		...(isAdmin
			? [
					{
						id: "connections" as const,
						label: t({
							id: "settings.connections",
							message: "Connections",
						}),
					},
				]
			: []),
		...(isAdmin
			? [
					{
						id: "tools" as const,
						label: t({ id: "settings.tools", message: "Tools" }),
					},
				]
			: []),
		{ id: "users", label: t({ id: "users", message: "Users" }) },
		{
			id: "preferences",
			label: t({ id: "settings.preferences", message: "Preferences" }),
		},
		...(isAdmin && developerEnabled
			? [
					{
						id: "developer" as const,
						label: t({ id: "settings.developer", message: "Developer" }),
					},
				]
			: []),
	];
	const active =
		sections.find((section) => section.id === requested)?.id || sections[0].id;
	useEffect(() => {
		const sync = () => setRequested(readSection());
		window.addEventListener("popstate", sync);
		return () => window.removeEventListener("popstate", sync);
	}, []);
	return (
		<div className="flex min-w-0 flex-col gap-6 md:flex-row">
			<nav
				aria-label={t({
					id: "settings.sections",
					message: "Settings sections",
				})}
				className="flex shrink-0 gap-1 overflow-x-auto md:w-48 md:flex-col md:self-start"
			>
				{sections.map((section) => (
					<a
						key={section.id}
						href={`?page=settings&section=${section.id}`}
						aria-current={active === section.id ? "page" : undefined}
						onClick={(event) => {
							if (
								event.button !== 0 ||
								event.metaKey ||
								event.ctrlKey ||
								event.shiftKey ||
								event.altKey
							)
								return;
							event.preventDefault();
							if (active === section.id) return;
							window.history.pushState(null, "", event.currentTarget.href);
							setRequested(section.id);
						}}
						className={`shrink-0 rounded-md px-4 py-3 text-sm font-medium whitespace-nowrap outline-none focus-visible:ring-2 focus-visible:ring-ring ${active === section.id ? "bg-accent text-accent-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
					>
						{section.label}
					</a>
				))}
			</nav>
			<div className="min-w-0 flex-1">
				<ExpandedSettingsPanels.Provider value={true}>
					<ActiveSection.Provider value={active}>
						{children}
					</ActiveSection.Provider>
				</ExpandedSettingsPanels.Provider>
			</div>
		</div>
	);
}

export function SettingsSection({
	id,
	children,
}: {
	id: Section;
	children: ReactNode;
}) {
	const active = useContext(ActiveSection);
	return (
		<div
			hidden={id !== active}
			className={id === active ? "space-y-6" : "hidden"}
		>
			{children}
		</div>
	);
}
